/// Renders terminal/hangar/apron polygons from a GeoJSON feature
/// collection. Used for the optional `buildingFileName` overlay
/// (n51_buildings, etc.) so airports look more like airports.

import { Container, Graphics } from 'pixi.js'
import type { CoordinateProjector } from '../geo/CoordinateProjector'
import type {
  AnyGeometry,
  FeatureCollection,
  MultiPolygon,
  Polygon,
  Position,
} from '../types/GeoJSON'
import { radarTheme } from './theme'

export class BuildingsLayer extends Container {
  constructor(features: FeatureCollection, projector: CoordinateProjector) {
    super()
    const g = new Graphics()
    this.addChild(g)
    for (const feat of features.features) {
      if (!feat.geometry) continue
      drawGeometry(g, feat.geometry, projector)
    }
    // A subtle outline; buildings should read as airport detail without
    // visually competing with runways or aircraft.
    g.fill({ color: radarTheme.landStroke, alpha: 0.4 })
    g.stroke({ width: 0.5, color: radarTheme.landStroke, alpha: 0.7 })
  }
}

function drawGeometry(
  g: Graphics,
  geom: AnyGeometry,
  projector: CoordinateProjector,
): void {
  switch (geom.type) {
    case 'Polygon':
      for (const ring of (geom as Polygon).coordinates) drawRing(g, ring, projector)
      break
    case 'MultiPolygon':
      for (const poly of (geom as MultiPolygon).coordinates) {
        for (const ring of poly) drawRing(g, ring, projector)
      }
      break
    default:
      break
  }
}

function drawRing(g: Graphics, ring: Position[], projector: CoordinateProjector): void {
  if (ring.length < 3) return
  let started = false
  for (const pos of ring) {
    const [lon, lat] = pos
    const p = projector.project({ latitude: lat, longitude: lon }, true)
    if (!started) {
      g.moveTo(p.x, p.y)
      started = true
    } else {
      g.lineTo(p.x, p.y)
    }
  }
  g.closePath()
}
